import React from 'react';
import { clsx } from 'clsx';
import { Control, Controller } from 'react-hook-form';
import { Select, SelectContent, SelectItem, SelectTrigger } from 'src/components/ui/select';
import type { CustomTemplateOption } from 'src/pages/ConfigurationPage/SystemPromptTab/SystemPromptTemplateSelect/SystemPromptTemplateSelect';

// The manual system prompt form of the onboarding and create-project steps.
export interface ManualPromptFormValues {
  text: string;
  template_id: string;
}

export const CustomTemplateSelect = ({
  name = 'template_id',
  label,
  control,
  options = [],
  onChange = () => {},
}: {
  name?: 'template_id';
  label?: string;
  control: Control<ManualPromptFormValues>;
  options?: CustomTemplateOption[];
  onChange?: (option: CustomTemplateOption | undefined) => void;
}) => {
  const [open, setOpen] = React.useState(false);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange: onSelectChange }, fieldState: { error } }) => {
        return (
          <div className={'w-full flex flex-col gap-2.5'}>
            <p className={clsx('text-sm text-ebony_opacity_50 font-medium', error && 'text-error')}>
              {label}
            </p>
            <Select
              defaultValue={value}
              value={value}
              open={open}
              onOpenChange={setOpen}
              onValueChange={(val) => {
                if (val) {
                  const option = options.find((option) => option.id === val);

                  onSelectChange(val);
                  onChange?.(option);
                }
              }}
            >
              <SelectTrigger
                className={clsx(
                  'w-full border py-2.5 px-4 bg-white rounded-sm-5 border-[#B8C1C633] drop-shadow-[0_2px_2px_rgba(0,0,0,0.25)] text-xs text-violet font-semibold',
                  error ? 'border-error text-error' : 'border-[#E3E6E8]',
                )}
              >
                <div>
                  <p className={'text-sm font-medium first-letter:capitalize'}>
                    <span>{options.find((option) => option.id === value)?.name || 'Custom'}</span>
                  </p>
                </div>
              </SelectTrigger>
              <SelectContent className={'bg-light z-[1000]'}>
                {options.map((option) => {
                  return (
                    <SelectItem
                      className={'px-3 py-3 rounded-none bg-white hover:!bg-[#a498ff1a]'}
                      key={option.id}
                      value={option.id}
                      indicatorHidden
                    >
                      <div className="flex flex-col">
                        <div className="flex gap-2 items-center">
                          <p
                            className={clsx(
                              'text-sm text-start relative first-letter:capitalize',
                              option.id === value ? 'font-semibold' : 'font-medium',
                            )}
                          >
                            <span>{option.name}</span>
                          </p>
                        </div>
                      </div>
                    </SelectItem>
                  );
                })}
                <SelectItem
                  className={'hidden px-3 py-3 rounded-none bg-white hover:!bg-[#a498ff1a]'}
                  value={'custom'}
                  indicatorHidden
                >
                  <div className="flex flex-col">
                    <div className="flex gap-2 items-center">
                      <p
                        className={clsx(
                          'text-sm text-start relative first-letter:capitalize',
                          value === 'custom' ? 'font-semibold' : 'font-medium',
                        )}
                      >
                        <span>Custom</span>
                      </p>
                    </div>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        );
      }}
    />
  );
};
